import { motion } from "framer-motion";
import { Reveal, Label } from "./motion";
import { IMAGES } from "@/data/content";

const SIZES = [
  {
    name: "Toy Poodle",
    weight: "Under 10 lbs",
    body: "Tiny frames need the smallest, most precise doses — where a calibrated tincture truly shines.",
    testid: "cbdoodle-poodle-size-toy",
  },
  {
    name: "Miniature Poodle",
    weight: "10 – 15 lbs",
    body: "Active minis benefit from steady daily support to keep those spry joints moving comfortably.",
    testid: "cbdoodle-poodle-size-mini",
  },
  {
    name: "Standard Poodle",
    weight: "45 – 70 lbs",
    body: "Larger bodies carry more on their joints with age — consistent comfort support matters most.",
    testid: "cbdoodle-poodle-size-standard",
  },
];

const DOODLE_BREEDS = [
  "Goldendoodle",
  "Labradoodle",
  "Bernedoodle",
  "Cockapoo",
  "Cavapoo",
  "Sheepadoodle",
  "Aussiedoodle",
  "Maltipoo",
  "Schnoodle",
  "Poochon",
  "Yorkipoo",
  "Cavoodle",
];

export const WhyPoodles = () => (
  <section id="why-poodles" data-testid="cbdoodle-why-poodles-section" className="bg-[#203028] px-5 py-20 sm:px-8 lg:py-28">
    <div className="mx-auto max-w-7xl">
      <div className="grid gap-12 lg:grid-cols-2 lg:items-center lg:gap-16">
        <div>
          <Reveal><Label className="text-[#C58F3B]">Why Doodles?</Label></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight text-[#FAF7F2] sm:text-4xl lg:text-[2.9rem]">
              It's in the name — made for poodles and every doodle they're bred into.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 max-w-md font-sans leading-relaxed text-[#C9D3CC]">
              Poodles are the heart of the doodle family. Goldendoodles, Labradoodles, Cockapoos, Bernedoodles
              and more all carry poodle genes — and face the same senior aches, restlessness, and worry. Whether
              toy, miniature, standard, or a designer doodle mix, CBDoodle is dosed by weight to fit them all.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 overflow-hidden rounded-[1.75rem] border border-white/10 shadow-2xl">
              <img
                src={IMAGES.whyPoodles}
                alt="Apricot poodle resting comfortably"
                className="h-64 w-full object-cover sm:h-80"
              />
            </div>
          </Reveal>
        </div>

        <div className="space-y-5">
          {SIZES.map((s, i) => (
            <Reveal key={s.name} delay={i * 0.1} data-testid={s.testid}>
              <motion.div
                whileHover={{ x: 6 }}
                transition={{ duration: 0.25 }}
                className="rounded-[1.5rem] border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm sm:p-7"
              >
                <div className="flex items-baseline justify-between gap-4">
                  <h3 className="font-serif text-2xl font-semibold text-[#FAF7F2]">{s.name}</h3>
                  <span className="flex-none rounded-full bg-[#C58F3B]/20 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.15em] text-[#E7C489]">
                    {s.weight}
                  </span>
                </div>
                <p className="mt-3 font-sans text-[15px] leading-relaxed text-[#C9D3CC]">{s.body}</p>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>

      <Reveal delay={0.15}>
        <div className="mt-14 rounded-[1.75rem] border border-white/10 bg-white/[0.04] p-7 sm:p-9">
          <p className="text-center font-mono text-[11px] uppercase tracking-[0.2em] text-[#E7C489]">
            The name says it all — a doodle for every doodle
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {DOODLE_BREEDS.map((b) => (
              <span
                key={b}
                data-testid={`cbdoodle-breed-${b.toLowerCase()}`}
                className="rounded-full border border-white/15 bg-white/[0.05] px-5 py-2.5 font-sans text-sm text-[#FAF7F2] transition-colors hover:border-[#C58F3B] hover:text-[#E7C489]"
              >
                {b}
              </span>
            ))}
          </div>
          <p className="mx-auto mt-6 max-w-2xl text-center font-sans text-sm leading-relaxed text-[#9fb0a7]">
            Poodle mix or purebred, big or small — if there's a doodle in your family, CBDoodle was made with
            them in mind.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);
